<template>
  <div class="automation-builder-wrapper">
    <!-- Breadcrumbs Section -->
    <div class="breadcrumb-section">
      <breadcrumb style="margin-left: -15px">
        <breadcrumb-item>
          <a href="/mail/custom/landing">Email templates</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>{{ automation.id ? 'Edit Automation' : 'New automation' }}</span>
        </breadcrumb-item>
      </breadcrumb>
    </div>

    <div class="automation-builder">
      <!-- Loading Panel -->
      <loading-panel :loading="loading"></loading-panel>

      <!-- Builder Header -->
      <div class="builder-header">
        <div class="header-left">
          <div class="automation-info">
            <h2 class="automation-title">{{ automation.name || 'New Automation' }}</h2>
          </div>
        </div>
        <div class="header-actions">
          <!-- Mobile sidebar buttons -->
          <div class="mobile-sidebar-controls">
            <base-button type="info" size="sm" simple @click="showMobileComponents = true" class="mobile-only">
              <i class="tim-icons icon-puzzle-10 mr-2"></i>
              Components
            </base-button>
            <base-button type="info" size="sm" simple @click="showMobileSettings = true" class="mobile-only">
              <i class="tim-icons icon-settings-gear-63 mr-2"></i>
              Settings
            </base-button>
          </div>
          <base-button type="info" size="sm" @click="saveAsDraft()">
            <i class="tim-icons icon-cloud-upload-94 mr-2"></i>
            Save Draft
          </base-button>
          <base-button type="primary" size="sm" @click="saveAndActivate()">
            <i class="tim-icons icon-check-2 mr-2"></i>
            Save & Activate
          </base-button>
        </div>
      </div>

      <!-- Main Builder Interface -->
      <div class="builder-container">
        <!-- Left Sidebar - Components -->
        <div class="components-sidebar desktop-only">
          <!-- Automation Settings -->
          <div class="sidebar-section">
            <h4 class="section-title">Automation Settings</h4>
            <div class="form-group">
              <label class="text-white opacity-7">Automation name</label>
              <base-input
                v-model="automation.name"
                placeholder="Automation name"
                maxlength="255"
                required>
              </base-input>
            </div>
            <div class="form-group">
              <label class="text-white opacity-7">Description</label>

              <base-input >
                <el-input
                  v-model="automation.description"
                  :rows="3"
                  type="textarea"
                  placeholder="Brief description of this automation..."
                  maxlength="500"
                  show-word-limit
                />
              </base-input>
            </div>
          </div>
        </div>

        <!-- Center Canvas Area -->
        <div class="canvas-area">
          <div class="canvas-header">
            <div class="canvas-title">
              <h5>
                <i class="tim-icons icon-molecule-40 mr-2"></i>
                Automation Flow
              </h5>
              <small class="text-muted">Design your workflow</small>
            </div>
            <div class="canvas-tools">
              <base-button type="primary"
                           size="sm"
                           simple
                           @click="clearFlow()"
                           v-if="automation.steps.length > 0 || hasTrigger"
              >
                <i class="tim-icons icon-trash-simple mr-2"></i> Clear
              </base-button>
            </div>
          </div>

          <div class="canvas-content">
            <!-- Empty State -->
            <div v-if="!hasTrigger && automation.steps.length === 0" class="empty-flow">
              <div class="empty-illustration">
                <i class="tim-icons icon-puzzle-10"></i>
              </div>
              <h4>Start building your automation</h4>
              <p>Add a trigger to get started, then build your workflow with actions.</p>
              <base-button type="primary" @click="openTriggerModal()">
                <i class="tim-icons icon-simple-add mr-1"></i> Add trigger
              </base-button>
            </div>

            <!-- Flow Steps -->
            <div v-else class="flow-canvas">
              <div class="flow-container">
                <!-- Trigger Step -->
                <div v-if="automation.trigger_type_id" class="flow-step trigger-step">
                  <div class="step-content">
                    <div class="step-header">
                      <div class="step-icon trigger">
                        <i :class="triggerType?.icon || 'tim-icons icon-bell-55'"></i>
                      </div>
                      <div class="step-info">
                        <div class="step-title-row">
                          <h6>{{ triggerType?.name || 'Trigger' }}</h6>
                          <badge type="primary" class="step-badge">START</badge>
                        </div>
                        <small class="step-description">{{ triggerType?.description }}</small>
                      </div>
                      <div class="step-actions">
                        <base-button type="primary" link icon size="sm" @click="openTriggerModal()">
                          <i class="tim-icons text-white icon-pencil"></i>
                        </base-button>
                        <base-button type="primary" link icon size="sm" @click="removeTrigger()">
                          <i class="tim-icons text-white icon-trash-simple"></i>
                        </base-button>
                      </div>
                    </div>
                    <div v-if="automation.trigger_config && Object.keys(automation.trigger_config).length" class="step-config">
                      <div class="config-badge">
                        <i class="tim-icons icon-settings-gear-63 mr-1"></i>
                        Configuration applied
                      </div>
                    </div>
                    <info-hint
                      v-if="triggerType && triggerType.trigger_key === 'abandon_cart' && !hasScheduleStep"
                      variant="warning"
                      icon="icon-watch-time"
                    >
                      Without a schedule component, this sends about <strong>60 minutes</strong> after the cart goes quiet. To change the timing, add a <strong>schedule</strong> step and set how long after the cart's last activity it should send.
                    </info-hint>
                    <info-hint
                      v-if="replacedSimpleEmails"
                      variant="neutral"
                      icon="icon-email-85"
                    >
                      Simple emails replaced while this automation is active: <strong>{{ replacedSimpleEmails.labels.join(', ') }}</strong><template v-if="replacedSimpleEmails.staff">, including the staff notification</template>. Replaced emails are not sent.
                    </info-hint>
                  </div>
                  <div class="flow-connector" v-if="automation.steps.length > 0">
                    <div class="connector-line"></div>
                    <div class="connector-dot"></div>
                  </div>
                </div>

                <!-- Action Steps -->
                <div
                  v-for="(step, index) in automation.steps"
                  :key="step.temp_id || step.id"
                  class="flow-step action-step"
                >
                  <div class="step-content">
                    <div class="step-header">
                      <div class="step-icon" :class="getStepTypeConfig(step.step_type).color">
                        <i :class="getStepTypeConfig(step.step_type).icon"></i>
                      </div>
                      <div class="step-info">
                        <div class="step-title-row">
                          <h6>{{ getStepTypeConfig(step.step_type).name }}</h6>
                        </div>
                        <small class="step-description">{{ getStepSummary(step) }}</small>
                      </div>
                      <div class="step-actions">
                        <base-button link icon size="sm" @click="editStep(step, index)">
                          <i class="tim-icons text-white icon-pencil"></i>
                        </base-button>
                        <base-button link icon size="sm" @click="removeStep(index)">
                          <i class="tim-icons text-white icon-trash-simple"></i>
                        </base-button>
                      </div>
                    </div>
                    <div class="config-preview text-white mt-3" v-if="getStepTypeConfig(step.step_type).name !== 'Schedule'">
                      {{ getStepConfigData(step).text }}
                    </div>
                  </div>

                  <!-- Connector to next step -->
                  <div class="flow-connector" v-if="index < automation.steps.length - 1">
                    <div class="connector-line"></div>
                    <div class="connector-dot"></div>
                  </div>
                </div>

                <!-- Add Step Button -->
                <div class="add-step-area" v-if="hasTrigger">
                  <div class="add-step-button" @click="showAddStepMenu = !showAddStepMenu">
                    <div class="add-step-icon">
                      <i class="tim-icons icon-simple-add"></i>
                    </div>
                    <span>Add step</span>
                  </div>

                  <!-- Add Step Menu -->
                  <div v-if="showAddStepMenu" class="add-step-menu">
                    <div class="step-option" @click="addStep('schedule')">
                      <div class="option-icon primary">
                        <i class="tim-icons icon-calendar-60"></i>
                      </div>
                      <div class="option-content">
                        <span>Schedule</span>
                        <small>Date-based timing</small>
                      </div>
                    </div>
                    <div class="step-option" @click="addStep('wait')">
                      <div class="option-icon warning">
                        <i class="tim-icons icon-time-alarm"></i>
                      </div>
                      <div class="option-content">
                        <span>Wait</span>
                        <small>Delay for time</small>
                      </div>
                    </div>
                    <div class="step-option" @click="addStep('condition')">
                      <div class="option-icon info">
                        <i class="tim-icons icon-check-2"></i>
                      </div>
                      <div class="option-content">
                        <span>Condition</span>
                        <small>Check criteria</small>
                      </div>
                    </div>
                    <div class="step-option" @click="addStep('send_email')">
                      <div class="option-icon success">
                        <i class="tim-icons icon-email-85"></i>
                      </div>
                      <div class="option-content">
                        <span>Send email</span>
                        <small>Email template</small>
                      </div>
                    </div>
                    <div v-if="smsEnabled" class="step-option" @click="addStep('send_sms')">
                      <div class="option-icon primary">
                        <i class="tim-icons icon-chat-33"></i>
                      </div>
                      <div class="option-content">
                        <span>Send SMS</span>
                        <small>Text message template</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Sidebar - Properties -->
        <div class="properties-sidebar desktop-only">
          <!-- Components Library -->
          <div class="sidebar-section">
            <h4 class="section-title">Components</h4>

            <!-- Trigger Section -->
            <div class="component-group">
              <h5 class="group-title">Trigger Event</h5>
              <p class="group-subtitle">Start your automation</p>
              <div
                class="component-item trigger-component"
                :class="{ disabled: hasTrigger, selected: hasTrigger }"
                @click="openTriggerModal()"
              >
                <i class="tim-icons icon-bell-55"></i>
                <div class="component-content">
                  <span class="component-name">When this happens</span>
                  <small class="component-desc">
                    {{ hasTrigger ? 'Trigger configured' : 'Choose trigger event' }}
                  </small>
                </div>
                <div class="component-status">
                  <i v-if="hasTrigger" class="tim-icons icon-check-2 text-success"></i>
                  <i v-else class="tim-icons icon-simple-add text-muted"></i>
                </div>
              </div>
            </div>

            <!-- Actions Section -->
            <div class="component-group">
              <h5 class="group-title">Actions</h5>
              <p class="group-subtitle">What happens next</p>
              <div class="components-grid">
                <div class="component-item" @click="addStep('schedule')">
                  <i class="tim-icons icon-calendar-60"></i>
                  <span>Schedule</span>
                </div>
                <div class="component-item" @click="addStep('condition')">
                  <i class="tim-icons icon-decision"></i>
                  <span>Condition</span>
                </div>
                <div class="component-item" @click="addStep('wait')">
                  <i class="tim-icons icon-time-alarm"></i>
                  <span>Wait</span>
                </div>
                <div class="component-item" @click="addStep('send_email')">
                  <i class="tim-icons icon-email-85"></i>
                  <span>Send Email</span>
                </div>
                <div v-if="smsEnabled" class="component-item" @click="addStep('send_sms')">
                  <i class="tim-icons icon-chat-33"></i>
                  <span>Send SMS</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Step Configuration -->
          <div v-if="selectedComponent" class="sidebar-section">
            <div class="section-header">
              <h4 class="section-title">
                {{ getSelectedItem()?.type === 'row' ? 'Row Layout' : 'Step Properties' }}
              </h4>
              <base-button type="danger" simple size="sm" @click="deleteSelectedComponent()">
                <i class="tim-icons icon-trash-simple"></i>
              </base-button>
            </div>
            <!-- Configuration forms will go here -->
          </div>
        </div>
      </div>

      <!-- Mobile Components Modal -->
      <modal
        :show.sync="showMobileComponents"
        body-classes="p-0"
        modal-classes="modal-dialog-centered modal-lg mobile-sidebar-modal"
      >
        <card
          type="secondary"
          header-classes="bg-white pb-2"
          body-classes="px-lg-3 py-lg-3"
          class="border-0 mb-0"
        >
          <div slot="header" class="modal-header-custom">
            <h4 class="modal-title">
              <i class="tim-icons icon-puzzle-10 mr-2"></i>
              Components
            </h4>
            <base-button type="secondary" simple size="sm" @click="showMobileComponents = false">
              <i class="tim-icons icon-simple-remove"></i>
            </base-button>
          </div>

          <div class="mobile-components-content">
            <!-- Trigger Section -->
            <div class="component-group">
              <h5 class="group-title">Trigger Event</h5>
              <p class="group-subtitle">Start your automation</p>
              <div
                class="component-item trigger-component"
                :class="{ disabled: hasTrigger, selected: hasTrigger }"
                @click="openTriggerModal(); showMobileComponents = false"
              >
                <i class="tim-icons icon-bell-55"></i>
                <div class="component-content">
                  <span class="component-name">When this happens</span>
                  <small class="component-desc">
                    {{ hasTrigger ? 'Trigger configured' : 'Choose trigger event' }}
                  </small>
                </div>
                <div class="component-status">
                  <i v-if="hasTrigger" class="tim-icons icon-check-2 text-success"></i>
                  <i v-else class="tim-icons icon-simple-add text-muted"></i>
                </div>
              </div>
            </div>

            <!-- Actions Section -->
            <div class="component-group">
              <h5 class="group-title">Actions</h5>
              <p class="group-subtitle">What happens next</p>
              <div class="components-grid mobile">
                <div class="component-item" @click="addStep('condition'); showMobileComponents = false">
                  <i class="tim-icons icon-decision"></i>
                  <span>Condition</span>
                </div>
                <div class="component-item" @click="addStep('wait'); showMobileComponents = false">
                  <i class="tim-icons icon-time-alarm"></i>
                  <span>Wait</span>
                </div>
                <div class="component-item" @click="addStep('send_email'); showMobileComponents = false">
                  <i class="tim-icons icon-email-85"></i>
                  <span>Send Email</span>
                </div>
              </div>
                <div v-if="smsEnabled" class="component-item" @click="addStep('send_sms'); showMobileComponents = false">
                  <i class="tim-icons icon-chat-33"></i>
                  <span>Send SMS</span>
                </div>
            </div>
          </div>
        </card>
      </modal>

      <!-- Mobile Settings Modal -->
      <modal
        :show.sync="showMobileSettings"
        body-classes="p-0"
        modal-classes="modal-dialog-centered modal-lg mobile-sidebar-modal"
      >
        <card
          type="secondary"
          header-classes="bg-white pb-2"
          body-classes="px-lg-3 py-lg-3"
          class="border-0 mb-0"
        >
          <div slot="header" class="modal-header-custom">
            <h4 class="modal-title">
              <i class="tim-icons icon-settings-gear-63 mr-2"></i>
              Automation Settings
            </h4>
            <base-button type="secondary" simple size="sm" @click="showMobileSettings = false">
              <i class="tim-icons icon-simple-remove"></i>
            </base-button>
          </div>

          <div class="mobile-settings-content">
            <div class="form-group">
              <base-input label="Automation name *">
                <input
                  type="text"
                  class="form-control"
                  v-model="automation.name"
                  placeholder="Enter automation name"
                  maxlength="255"
                  required
                >
              </base-input>
            </div>
            <div class="form-group">
              <base-input label="Description">
                <el-input
                  v-model="automation.description"
                  :rows="3"
                  type="textarea"
                  placeholder="Brief description of this automation..."
                  maxlength="500"
                  show-word-limit
                />
              </base-input>
            </div>

            <!-- Step Configuration for mobile -->
            <div v-if="selectedComponent" class="sidebar-section">
              <div class="section-header">
                <h4 class="section-title">
                  {{ getSelectedItem()?.type === 'row' ? 'Row Layout' : 'Step Properties' }}
                </h4>
                <base-button type="danger" simple size="sm" @click="deleteSelectedComponent()">
                  <i class="tim-icons icon-trash-simple"></i>
                </base-button>
              </div>
              <!-- Configuration forms will go here -->
            </div>
          </div>

          <div slot="footer" class="text-center">
            <base-button type="primary" @click="showMobileSettings = false">
              Done
            </base-button>
          </div>
        </card>
      </modal>

      <!-- Trigger Selection Modal -->
      <modal
        :show.sync="modals.trigger"
        body-classes="p-0"
        modal-classes="modal-dialog-centered modal-xl modal-offset-left"
      >
        <card
          type="secondary"
          header-classes="bg-white pb-5"
          body-classes="px-lg-5 py-lg-5 modal-xl"
          class="border-0 mb-0"
        >
          <h4 class="card-title">
            Select trigger
          </h4>

          <div class="trigger-categories">
            <div class="row">
              <div class="col-md-4">
                <div class="category-list">
                  <div
                    v-for="category in triggerCategories"
                    :key="category.key"
                    class="category-item"
                    :class="{ active: selectedCategory === category.key }"
                    @click="selectedCategory = category.key"
                  >
                    <div class="category-icon">
                      <i :class="category.icon"></i>
                    </div>
                    <div class="category-content">
                      <span class="category-name">{{ category.name }}</span>
                      <badge class="category-count">{{ category.count }}</badge>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-8">
                <div class="triggers-list">
                  <div
                    v-for="trigger in filteredTriggers"
                    :key="trigger.id"
                    class="trigger-option"
                    :class="{ selected: selectedTrigger?.id === trigger.id }"
                    @click="selectedTrigger = trigger"
                  >
                    <div class="trigger-info">
                      <div class="trigger-icon">
                        <i :class="trigger.icon"></i>
                      </div>
                      <div class="trigger-details">
                        <h6>{{ trigger.name }}</h6>
                        <p>{{ trigger.description }}</p>
                      </div>
                    </div>
                    <div class="trigger-select">
                      <i class="tim-icons icon-check-2" v-if="selectedTrigger?.id === trigger.id"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div slot="footer" class="text-center">
            <base-button type="secondary" @click="modals.trigger = false">Cancel</base-button>
            <base-button
              type="primary"
              @click="applyTrigger()"
              :disabled="!selectedTrigger"
              class="ml-2"
            >
              Add trigger
            </base-button>
          </div>
        </card>
      </modal>

      <!-- Step Configuration Modal -->
      <modal
        :show.sync="modals.stepConfig"
        body-classes="p-0"
        :modal-classes="getModalSize(currentStepType?.type)"
      >
        <!-- pb-5 / py-lg-5 left ~6rem of empty space stacked under the title and
             around short forms. Shared by every step type's config modal, so
             this tightens all of them, not just Send SMS. -->
        <card
          header-classes="pb-2"
          body-classes="px-lg-5 py-lg-4"
          class="border-0 mb-0"
        >
          <h4 class="card-title">
            {{ editingStep ? 'Edit' : 'Configure' }} {{ currentStepType?.name }} component
          </h4>

          <div class="step-configuration">
            <!-- Wait Step Config -->
            <div v-if="currentStepType?.type === 'wait'" class="config-section">
              <div class="config-header">
                <small class="text-white">Set how long to wait before continuing</small>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <base-input label="Duration">
                    <input
                      type="number"
                      class="form-control"
                      v-model="stepConfig.duration"
                      min="1"
                      placeholder="5"
                    >
                  </base-input>
                </div>
                <div class="col-md-6">
                  <base-input label="Unit">
                    <el-select
                      v-model="stepConfig.unit"
                      class="select-primary"
                      placeholder="Select unit"
                    >
                      <el-option
                        class="select-primary"
                        label="Minutes"
                        value="minutes"
                      ></el-option>
                      <el-option
                        class="select-primary"
                        label="Hours"
                        value="hours"
                      ></el-option>
                      <el-option
                        class="select-primary"
                        label="Days"
                        value="days"
                      ></el-option>
                      <el-option
                        class="select-primary"
                        label="Weeks"
                        value="weeks"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
            </div>

            <!-- Enhanced Condition Step Configuration -->
            <div v-if="currentStepType?.type === 'condition'" class="config-section">
              <div class="config-header">
                <small class="text-white">Define the criteria that must be met for this automation to continue. The automation stops if the criteria are not met.<template v-if="triggerType && triggerType.category === 'project'"> The <strong>Staff</strong> field refers to the team member who performed the action that triggered this automation, not the ticket's assignee, reporter, or watchers. To email those people instead, choose them directly as recipients in the Send email step. To email the team when a ticket reaches a status such as Done, use the <strong>Ticket status changed</strong> trigger with a <strong>Status</strong> condition.</template></small>
              </div>

              <!-- Logic Operator for Multiple Conditions -->
              <div v-if="stepConfig.conditions && stepConfig.conditions.length > 1" class="row mb-3">
                <div class="col">
                  <base-input label="Logic">
                    <el-select
                      v-model="stepConfig.logic"
                      class="select-primary"
                      placeholder="Select logic"
                    >
                      <el-option class="select-primary" label="ALL conditions must be met (AND)" value="AND"></el-option>
                      <el-option class="select-primary" label="ANY condition must be met (OR)" value="OR"></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <!-- Condition Rules -->
              <div class="conditions-container">
                <div
                  v-for="(condition, index) in stepConfig.conditions"
                  :key="index"
                  class="condition-rule"
                >
                  <div class="condition-header">
                    <span class="condition-number">{{ index + 1 }}</span>
                    <span class="condition-label text-white">
                      {{ index === 0 ? 'If' : (stepConfig.logic === 'AND' ? 'And' : 'Or') }}
                    </span>
                    <base-button
                      v-if="stepConfig.conditions.length > 1"
                      simple
                      link
                      size="sm"
                      @click="removeCondition(index)"
                      class="ml-auto"
                    >
                      <i class="tim-icons text-white icon-simple-remove"></i>
                    </base-button>
                  </div>

                  <div class="condition-builder">
                    <!-- Field Selection -->
                    <div class="condition-field">
                      <label class="text-white opacity-7">Field</label>
                      <el-select
                        v-model="condition.field"
                        class="select-primary"
                        placeholder="Select field to check"
                        @change="onConditionFieldChange(condition, $event)"
                        filterable
                        value-key="id"
                      >
                        <el-option
                          v-for="field in filteredConditionFields"
                        :key="field.field_key"
                        class="select-primary"
                        :label="field.field_name"
                        :value="field.field_key"
                        >
                          <div>{{ field.field_name }}
                            <small class="text-default small ml-2">{{ field.field_description }}</small>
                          </div>
                        </el-option>
                      </el-select>
                    </div>

                    <!-- Operator Selection -->
                    <div class="condition-operator">
                      <label class="text-white opacity-7">Condition</label>
                      <el-select
                        v-model="condition.operator"
                        class="select-primary"
                        placeholder="Select condition"
                        @change="onConditionOperatorChange(condition, $event)"
                        :disabled="!condition.field"
                      >
                        <el-option
                          v-for="operator in getOperatorsForField(condition.field)"
                          :key="operator"
                          class="select-primary"
                          :label="formatOperator(operator)"
                          :value="operator"
                        ></el-option>
                      </el-select>
                    </div>
                    <!-- Value Selection -->
                    <div class="condition-value" v-if="condition.operator && !['exists', 'not_exists'].includes(condition.operator)">
                      <label class="text-white opacity-7">Value</label>

                      <!-- Game Selection Dropdown -->
                      <el-select
                        v-if="condition.field.includes('game_id')"
                        v-model="condition.value"
                        class="select-primary"
                        placeholder="Select game"
                        filterable
                        value-key="id"
                      >
                        <el-option
                          v-for="game in games"
                          :key="game.id"
                          class="select-primary"
                          :label="game.name"
                          :value="game.id"
                        >
                        </el-option>
                      </el-select>

                      <el-select
                        v-else-if="condition.field.includes('game_master_id')"
                        v-model="condition.value"
                        class="select-primary"
                        placeholder="Select game master"
                        filterable
                        value-key="id"
                      >
                        <el-option
                          v-for="cu in companyUsers"
                          :key="cu.id + 'CUC'"
                          class="select-primary"
                          :label="cu.email"
                          :value="cu.id"
                        >
                        </el-option>
                      </el-select>

                      <el-select
                        v-else-if="condition.field.includes('gift_card_type')"
                        v-model="condition.value"
                        class="select-primary"
                        placeholder="Select gift card type"
                        filterable
                        value-key="id"
                      >
                        <el-option
                          v-for="gct in giftCardTypes"
                          :key="gct.id + 'GCT'"
                          class="select-primary"
                          :label="gct.name"
                          :value="gct.id"
                        >
                        </el-option>
                      </el-select>

                      <!-- Predefined Values Dropdown -->
                      <el-select
                        v-else-if="getPossibleValues(condition.field).length > 0"
                        v-model="condition.value"
                        class="select-primary"
                        placeholder="Select value"
                      >
                        <el-option
                          v-for="value in getPossibleValues(condition.field)"
                          :key="value"
                          class="select-primary"
                          :label="formatValue(value, getFieldConfig(condition.field))"
                          :value="value"
                        ></el-option>
                      </el-select>

                      <!-- Between Range Inputs -->
                      <div v-else-if="condition.operator === 'between'" class="between-inputs">
                        <input
                          type="number"
                          class="form-control"
                          placeholder="Min"
                          v-model="condition.minValue"
                          @input="updateBetweenValue(condition)"
                        >
                        <span class="between-separator">and</span>
                        <input
                          type="number"
                          class="form-control"
                          placeholder="Max"
                          v-model="condition.maxValue"
                          @input="updateBetweenValue(condition)"
                        >
                      </div>

                      <!-- Free Text Input -->
                      <input
                        v-else
                        type="text"
                        class="form-control"
                        placeholder="Enter value"
                        v-model="condition.value"
                      >
                    </div>
                  </div>

                  <!-- Condition Preview -->
                  <div v-if="isConditionValid(condition)" class="condition-preview">
                    <i class="tim-icons icon-check-2 text-success mr-2"></i>
                    <span class="condition-text text-white">{{ getConditionDescription(condition) }}</span>
                  </div>
                </div>
              </div>

              <!-- Add Condition Button -->
              <base-button size="sm" type="default" @click="addCondition()" class="mb-4">
                <i class="tim-icons icon-simple-add mr-1"></i> Add another condition
              </base-button>

            </div>

            <!-- Add this inside your Step Configuration Modal, after the wait and condition configs -->

            <!-- Schedule Step Config -->
            <div v-if="currentStepType?.type === 'schedule'" class="config-section">
              <div class="config-header">
                <small class="text-white">Schedule when this action should occur based on dates and times</small>
              </div>

              <!-- Reference Field Selection (shown first so fixed fields can hide schedule type) -->
              <div v-if="stepConfig.schedule_type !== 'specific_time'" class="row mb-3">
                <div class="col-md-12">
                  <base-input label="Reference date field" required>
                    <el-select
                      v-model="stepConfig.reference_field"
                      class="select-primary"
                      placeholder="Select date field"
                      filterable
                      @change="onReferenceFieldChange"
                    >
                      <el-option
                        v-for="field in getScheduleFields()"
                        :key="field.field_key"
                        class="select-primary"
                        :label="field.field_name"
                        :value="field.field_key"
                      >
                        <div>
                          {{ field.field_name }}
                        </div>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <!-- Schedule Type Selection (hidden when reference field locks to on_date) -->
              <div v-if="!isFixedOnDateField" class="row mb-3">
                <div class="col-md-12">
                  <base-input label="Schedule type" required>
                    <el-select
                      v-model="stepConfig.schedule_type"
                      class="select-primary"
                      placeholder="Select schedule type"
                      @change="onScheduleTypeChange"
                    >
                      <el-option class="select-primary" label="Before a date" value="before_date"></el-option>
                      <el-option class="select-primary" label="After a date" value="after_date"></el-option>
<!--                      <el-option class="select-primary" label="On a specific date" value="on_date"></el-option>-->
<!--                      <el-option class="select-primary" label="At a specific time" value="specific_time"></el-option>-->
                    </el-select>
                  </base-input>
                </div>
              </div>

              <!-- Offset Configuration (shown for before_date and after_date) -->
              <div v-if="['before_date', 'after_date'].includes(stepConfig.schedule_type)" class="row mb-3">
                <div class="col-md-6">
                  <base-input label="Time amount" required>
                    <input
                      type="number"
                      class="form-control"
                      v-model="stepConfig.offset.value"
                      min="1"
                      placeholder="e.g., 24"
                    >
                  </base-input>
                </div>
                <div class="col-md-6">
                  <base-input label="Time unit" required>
                    <el-select
                      v-model="stepConfig.offset.unit"
                      class="select-primary"
                      placeholder="Select unit"
                    >
                      <el-option class="select-primary" label="Minutes" value="minutes"></el-option>
                      <el-option class="select-primary" label="Hours" value="hours"></el-option>
                      <el-option class="select-primary" label="Days" value="days"></el-option>
                      <el-option class="select-primary" label="Weeks" value="weeks"></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <!-- Schedule Preview -->
              <div v-if="validateScheduleConfig(stepConfig)" class="schedule-preview mt-3">
                <div class="preview-card">
                  <i class="tim-icons icon-calendar-60 text-primary mr-2"></i>
                  <span class="preview-text text-white">
                    {{ formatScheduleDescription(stepConfig) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Send Email Step Config -->
            <div v-if="currentStepType?.type === 'send_sms'" class="config-section">
              <div class="row">
                <div class="col-md-12">
                  <base-input label="Select template *">
                    <el-select
                      v-model="stepConfig.sms_template_id"
                      class="select-primary"
                      placeholder="Choose SMS template"
                    >
                      <el-option
                        v-for="template in smsTemplates"
                        :key="template.id"
                        class="select-primary"
                        :label="template.name"
                        :value="template.id"
                      ></el-option>
                    </el-select>
                  </base-input>
                  <info-hint v-if="!smsTemplates.length" icon="icon-alert-circle-exc" variant="warning">
                    No active SMS templates yet. Create one on the SMS templates tab first.
                  </info-hint>
                  <info-hint v-if="smsUnresolvedVariables.length" icon="icon-alert-circle-exc" variant="warning">
                    This trigger cannot fill {{ smsUnresolvedVariables.join(', ') }}.
                    Those will be sent to the customer exactly as written, and still cost a segment.
                  </info-hint>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Send to">
                    <el-select
                      v-model="stepConfig.recipient_type"
                      class="select-primary"
                      placeholder="Select recipient"
                    >
                      <el-option class="select-primary" label="Customer phone" value="customer_phone"></el-option>
                      <el-option class="select-primary" label="Custom phone number" value="custom_phone"></el-option>
                      <el-option class="select-primary" label="Staff phone" value="company_user_phone"></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <div v-if="stepConfig.recipient_type === 'custom_phone'" class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Custom phone number">
                    <input
                      type="tel"
                      class="form-control"
                      v-model="stepConfig.custom_phone"
                      placeholder="(555) 123-4567"
                    >
                  </base-input>
                  <info-hint icon="icon-alert-circle-exc" variant="warning">
                    Only text numbers whose owner has agreed to receive messages from this
                    business. Consent is not checked for a number typed in here.
                  </info-hint>
                </div>
              </div>

              <div v-if="stepConfig.recipient_type === 'company_user_phone'" class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Staff members">
                    <el-select
                      v-model="stepConfig.company_users"
                      class="select-primary"
                      multiple
                      placeholder="Choose staff"
                    >
                      <el-option
                        v-for="user in companyUsers"
                        :key="user.id"
                        class="select-primary"
                        :label="user.first_name + ' ' + user.last_name"
                        :value="user.id"
                      ></el-option>
                    </el-select>
                  </base-input>
                  <info-hint icon="icon-bulb-63" variant="neutral">
                    Staff who turned off SMS alerts on their profile are skipped automatically.
                  </info-hint>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-12">
                  <div class="sms-step-toggle">
                    <el-switch
                      v-model="stepConfig.fallback_to_email"
                      active-color="#e14eca"
                    />
                    <!-- "cannot be sent", not "cannot be delivered": the fallback
                         fires on what we know at send time (no number on file,
                         consent withheld). A text the carrier accepts and later
                         fails to deliver reports back asynchronously and does
                         not trigger this. -->
                    <label class="text-white mb-0">Send an email instead when the text cannot be sent</label>
                  </div>
                  <info-hint icon="icon-bulb-63" variant="neutral">
                    Applies when the customer has no phone number on file, or has not agreed to
                    receive texts. It does not cover a message the phone carrier rejects after
                    sending.
                  </info-hint>
                </div>
              </div>

              <div v-if="stepConfig.fallback_to_email" class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Fallback email template">
                    <el-select
                      v-model="stepConfig.fallback_email_template_id"
                      class="select-primary"
                      placeholder="Choose email template"
                    >
                      <el-option
                        v-for="template in emailTemplates"
                        :key="template.id"
                        class="select-primary"
                        :label="template.name"
                        :value="template.id"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
            </div>

            <div v-if="currentStepType?.type === 'send_email'" class="config-section">
              <div class="row">
                <div class="col-md-12">
                  <base-input label="Select template *">
                    <el-select
                      v-model="stepConfig.template_id"
                      class="select-primary"
                      placeholder="Choose email template"
                    >
                      <el-option
                        v-for="template in emailTemplates"
                        :key="template.id"
                        class="select-primary"
                        :label="template.name"
                        :value="template.id"
                      ></el-option>
                    </el-select>
                  </base-input>
                  <info-hint v-if="emailUnresolvedVariables.length" icon="icon-alert-circle-exc" variant="warning">
                    This trigger cannot fill {{ emailUnresolvedVariables.join(', ') }}.
                    Those will appear in the email exactly as written.
                  </info-hint>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Send to">
                    <el-select
                      v-model="stepConfig.to_field"
                      class="select-primary"
                      placeholder="Select recipient"
                    >
                      <el-option v-if="!triggerType || triggerType.category !== 'project'" class="select-primary" label="Customer email" value="customer_email"></el-option>
                      <el-option v-if="triggerType && triggerType.category === 'gift_card'" class="select-primary" label="Gift card recipient email" value="gift_card_recipient"></el-option>
                      <el-option class="select-primary" label="Custom email" value="custom"></el-option>
                      <el-option class="select-primary" label="Company user email" value="company_email"></el-option>
                      <el-option v-if="isTicketTrigger" class="select-primary" label="Ticket watchers" value="ticket_watchers"></el-option>
                      <el-option v-if="isTicketTrigger" class="select-primary" label="Ticket assignee" value="ticket_assignee"></el-option>
                      <el-option v-if="isTicketTrigger" class="select-primary" label="Ticket reporter" value="ticket_reporter"></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <!-- Show custom email input when custom is selected -->
              <div v-if="stepConfig.to_field === 'custom'" class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Custom email address">
                    <input
                      type="email"
                      class="form-control"
                      placeholder="email@example.com"
                      v-model="stepConfig.custom_email"
                    >
                  </base-input>
                </div>
              </div>

              <!-- Show company users dropdown when company_email is selected -->
              <div v-if="stepConfig.to_field === 'company_email'" class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Select company users">
                    <el-select
                      v-model="stepConfig.company_users"
                      class="select-primary"
                      placeholder="Select company users"
                      multiple
                      filterable
                      value-key="id"
                    >
                      <el-option
                        v-for="user in companyUsers"
                        :key="user.id"
                        class="select-primary"
                        :label="formatUserLabel(user)"
                        :value="user"
                      >
                        <div>{{formatUserLabel(user)}}</div>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <!-- Show roles dropdown when company_email is selected -->
              <div v-if="stepConfig.to_field === 'company_email'" class="row mt-3">
                <div class="col-md-12">
                  <base-input label="Select roles">
                    <el-select
                      v-model="stepConfig.company_roles"
                      class="select-primary"
                      placeholder="Select roles"
                      multiple
                      filterable
                      value-key="role_key"
                    >
                      <el-option
                        v-for="role in companyRoles"
                        :key="role.role_key"
                        class="select-primary"
                        :label="role.name"
                        :value="role"
                      >
                        <div>{{role.name}} <badge class="ml-2" v-if="role.default_role" type="primary">default</badge></div>
                      </el-option>
                    </el-select>
                    <small class="text-white d-block mt-1">Sends to every active user in this group with the selected role, resolved when the email is sent.</small>
                  </base-input>
                </div>
              </div>
            </div>
          </div>

          <div slot="footer" class="text-center">
            <base-button type="secondary" @click="modals.stepConfig = false">Cancel</base-button>
            <base-button
              type="primary"
              @click="saveStepConfig()"
              :disabled="!isStepConfigValid"
              class="ml-2"
            >
              {{ editingStep ? 'Update step' : 'Add step' }}
            </base-button>
          </div>
        </card>
      </modal>
    </div>
  </div>
</template>

<script>
import { Badge } from "@/components";
import { API_LOCATION } from "@/api-config";
import { isSmsBetaCompany } from '@/util/smsBeta'
import axios from "axios";
import swal from "sweetalert2";
import { Auth } from "aws-amplify";
import {Input, Select, Option} from "element-ui";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import InfoHint from "@/components/InfoHint.vue";
import { unresolvedVariables } from "@/util/automationVariables";
import { replacedByTrigger } from "@/util/automationReplacesSimpleEmails";

const TICKET_TRIGGER_KEYS = ['ticket_created', 'ticket_updated', 'ticket_status_changed']
function isTicketTriggerKey(key) {
  return !!key && (TICKET_TRIGGER_KEYS.includes(key) || key.startsWith('comment_'))
}

export default {
  name: "EmailAutomationBuilder",
  components: {
    Badge,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    BreadcrumbItem,
    Breadcrumb,
    InfoHint
  },
  data() {
    return {
      companyUsers: [],
      companyRoles: [],
      giftCardTypes: [],
      scheduleFields: [],
      games: [], // New: store games data
      loading: false,
      isEditing: false,
      automationId: null,
      selectedComponent: null,

      // Mobile state
      showMobileComponents: false,
      showMobileSettings: false,

      conditionBuilder: {
        selectedField: null,
        availableOperators: [],
        availableValues: [],
        showValuePicker: false
      },

      // Main automation object
      automation: {
        id: null,
        name: '',
        description: '',
        trigger_type_id: null,
        trigger_config: {},
        steps: [],
        is_active: false
      },

      // Available data
      triggerTypes: [],
      emailTemplates: [],
      smsTemplates: [],
      conditionFields: [],

      // UI state
      showAddStepMenu: false,
      selectedCategory: 'booking',
      selectedTrigger: null,

      // Step configuration
      currentStepType: null,
      editingStep: null,
      editingStepIndex: null,
      stepConfig: {},

      // Modals
      modals: {
        trigger: false,
        stepConfig: false
      },

      // Company info
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,

      // Step type configurations
      stepTypes: {
        wait: {
          name: 'Wait',
          icon: 'tim-icons icon-time-alarm',
          color: 'warning',
          defaultConfig: {
            duration: 5,
            unit: 'minutes'
          }
        },
        condition: {
          name: 'Condition',
          icon: 'tim-icons icon-decision-white',
          color: 'info',
          defaultConfig: {
            logic: 'AND', // AND/OR for multiple conditions
            conditions: [
              {
                field: '',
                operator: 'equals',
                value: '',
                value_type: 'static' // 'static', 'field', 'variable'
              }
            ],
          }
        },
        schedule: {
          name: 'Schedule',
          icon: 'tim-icons icon-calendar-60',
          color: 'primary',
          defaultConfig: {
            schedule_type: 'before_date', // 'before_date', 'after_date', 'on_date', 'specific_time'
            reference_field: '', // e.g., 'booking.start_date'
            offset: {
              value: 24,
              unit: 'hours', // 'minutes', 'hours', 'days', 'weeks'
              direction: 'before' // 'before', 'after'
            },
            time_constraints: {
              specific_time: null, // e.g., '10:00' for 10 AM
              timezone: 'business', // 'customer', 'business', 'UTC'
              skip_weekends: false,
              skip_holidays: false,
              business_hours_only: false,
              business_hours: {
                start: '09:00',
                end: '17:00'
              }
            },
            fallback_behavior: 'skip' // 'skip', 'send_immediately', 'next_valid_time'
          }
        },
        send_email: {
          name: 'Send email',
          icon: 'tim-icons icon-email-85',
          color: 'success',
          defaultConfig: {
            template_id: '',
            to_field: 'customer_email',
            custom_email: '',
            company_users: null,
            company_roles: null
          }
        },
        // SMS channel. step_config shape is consumed by
        // sms-send-from-automation (recipient_type, sms_template_id,
        // fallback_to_email); consent is enforced downstream in sms-send-raw.
        send_sms: {
          name: 'Send SMS',
          icon: 'tim-icons icon-chat-33',
          color: 'primary',
          defaultConfig: {
            sms_template_id: '',
            recipient_type: 'customer_phone',
            custom_phone: '',
            company_users: null,
            fallback_to_email: false,
            fallback_email_template_id: ''
          }
        }
      }
    }
  },
  watch: {
    'stepConfig.to_field'(newVal, oldVal) {
      if (newVal !== 'custom') {
        this.stepConfig.custom_email = ''
      }
      if (newVal !== 'company_email') {
        this.stepConfig.company_users = null
        this.stepConfig.company_roles = null
      }
    },
    'automation.trigger_type_id'(newVal, oldVal) {
      // When trigger changes, reset all condition steps
      if (oldVal && newVal !== oldVal) {
        this.automation.steps = this.automation.steps.map(step => {
          if (step.step_type === 'condition') {
            // Reset the condition configuration
            return {
              ...step,
              step_config: {
                logic: 'AND',
                conditions: [
                  {
                    field: '',
                    operator: 'equals',
                    value: '',
                    value_type: 'static'
                  }
                ]
              }
            }
          }
          if (step.step_type === 'send_email') {
            const newTrigger = this.triggerTypes.find(t => t.id === newVal)
            const isProject = newTrigger && newTrigger.category === 'project'
            const isTicket = newTrigger && isTicketTriggerKey(newTrigger.trigger_key)
            if (isProject && step.step_config.to_field === 'customer_email') {
              return { ...step, step_config: { ...step.step_config, to_field: '', company_users: null, company_roles: null } }
            }
            if (!isTicket && ['ticket_watchers', 'ticket_assignee', 'ticket_reporter'].includes(step.step_config.to_field)) {
              return { ...step, step_config: { ...step.step_config, to_field: '' } }
            }
          }
          if (step.step_type === 'schedule') {
            return {
              ...step,
              step_config: {
                schedule_type: 'before_date',
                reference_field: '', // Clear the reference field
                offset: {
                  value: 24,
                  unit: 'hours',
                  direction: 'before'
                },
                time_constraints: {
                  specific_time: null,
                  timezone: 'business',
                  skip_weekends: false,
                  skip_holidays: false,
                  business_hours_only: false,
                  business_hours: {
                    start: '09:00',
                    end: '17:00'
                  }
                },
                fallback_behavior: 'skip'
              }
            }
          }
          return step
        })
      }
    }
  },
  computed: {
    // SMS actions appear only for groups that finished carrier registration
    // (group.sms_active, surfaced by companies-get-company-groups) AND are in
    // the SMS beta. Everything SMS in this builder hangs off this one gate.
    smsEnabled() {
      try {
        const group = JSON.parse(localStorage.getItem('group') || '{}')
        return !!group.sms_active && isSmsBetaCompany()
      } catch (e) {
        return false
      }
    },
    filteredConditionFields() {
      // If no trigger is selected, return empty array
      if (!this.triggerType) {
        return []
      }

      // Get the category from the selected trigger
      const triggerCategory = this.triggerType.category

      // Filter condition fields based on the category
      return this.conditionFields.filter(field => {
        // Check if the field_key contains the category name
        return field.field_key.toLowerCase().includes(triggerCategory.toLowerCase())
      })
    },
    lastStepIsSendEmail() {
      if (this.automation.steps.length === 0) return false
      const lastStep = this.automation.steps[this.automation.steps.length - 1]
      return lastStep.step_type === 'send_email'
    },
    // Get operators for selected field
    getOperatorsForField() {
      return (fieldKey) => {
        // Special handling for game_id field
        if (fieldKey === 'game_id') {
          return ['equals', 'not_equals', 'in', 'not_in']
        }

        const field = this.conditionFields.find(f => f.field_key === fieldKey)
        return field ? field.available_operators || [] : []
      }
    },

    // Get possible values for selected field
    getPossibleValues() {
      return (fieldKey) => {
        // Game field doesn't use this - it has its own select
        if (fieldKey === 'game_id') {
          return []
        }

        const field = this.conditionFields.find(f => f.field_key === fieldKey)
        return field ? field.possible_values || [] : []
      }
    },

    hasTrigger() {
      return !!this.automation.trigger_type_id
    },

    triggerType() {
      return this.triggerTypes.find(t => t.id === this.automation.trigger_type_id)
    },

    isFixedOnDateField() {
      const fixedFields = ['gift_card.delivery_date']
      return fixedFields.includes(this.stepConfig.reference_field)
    },

    isTicketTrigger() {
      if (!this.triggerType) return false
      return isTicketTriggerKey(this.triggerType.trigger_key)
    },

    triggerCategories() {
      const categories = {}
      this.triggerTypes.forEach(trigger => {
        if (!categories[trigger.category]) {
          categories[trigger.category] = {
            key: trigger.category,
            name: this.formatCategoryName(trigger.category),
            icon: this.getCategoryIcon(trigger.category),
            count: 0
          }
        }
        categories[trigger.category].count++
      })
      return Object.values(categories)
    },

    filteredTriggers() {
      return this.triggerTypes.filter(trigger => trigger.category === this.selectedCategory)
    },

    hasScheduleStep() {
      return this.automation.steps.some(step => step.step_type === 'schedule')
    },

    // Simple emails this trigger takes the place of while the automation is active
    replacedSimpleEmails() {
      return this.triggerType ? replacedByTrigger(this.triggerType.trigger_key) : null
    },

    // Variables the chosen template uses that this trigger cannot supply. The
    // renderer leaves them as literal {{tokens}} in the delivered message, so
    // this is the only place a venue can catch it before a customer does.
    // Silent when the trigger has no usable field metadata (see
    // sectionsForTrigger) rather than warning on everything.
    smsUnresolvedVariables() {
      if (!this.selectedTrigger || !this.stepConfig.sms_template_id) return []
      const template = this.smsTemplates.find(t => t.id === this.stepConfig.sms_template_id)
      if (!template) return []
      return unresolvedVariables(template.body, this.selectedTrigger.available_fields)
    },
    emailUnresolvedVariables() {
      if (!this.selectedTrigger || !this.stepConfig.template_id) return []
      const template = this.emailTemplates.find(t => t.id === this.stepConfig.template_id)
      if (!template) return []
      // Variables can sit in the subject, the preview text or anywhere in the
      // component tree, so all three are scanned.
      let content = ''
      try {
        content = typeof template.content_json === 'string'
          ? template.content_json
          : JSON.stringify(template.content_json || '')
      } catch (e) {
        content = ''
      }
      const haystack = [template.subject_line, template.preview_text, content].filter(Boolean).join(' ')
      return unresolvedVariables(haystack, this.selectedTrigger.available_fields)
    },
    isStepConfigValid() {
      if (!this.currentStepType) return false

      switch (this.currentStepType.type) {
        case 'wait':
          return this.stepConfig.duration && this.stepConfig.unit
        case 'schedule':
          return this.validateScheduleConfig(this.stepConfig)
        case 'condition':
          return this.stepConfig.conditions &&
            this.stepConfig.conditions.length > 0 &&
            this.stepConfig.conditions.every(c => this.isConditionValid(c))

        case 'send_email':
          const hasTemplate = !!this.stepConfig.template_id
          const hasRecipient = !!this.stepConfig.to_field
          const hasCustomEmail = this.stepConfig.to_field !== 'custom' || !!this.stepConfig.custom_email
          const hasCompanyUser = this.stepConfig.to_field !== 'company_email' ||
            (this.stepConfig.company_users && this.stepConfig.company_users.length > 0) ||
            (this.stepConfig.company_roles && this.stepConfig.company_roles.length > 0)

          return hasTemplate && hasRecipient && hasCustomEmail && hasCompanyUser

        // Braced: the send_email case above declares consts directly in the
        // switch's block scope, so unbraced consts here would be a duplicate
        // declaration.
        case 'send_sms': {
          const hasSmsTemplate = !!this.stepConfig.sms_template_id
          const recipientType = this.stepConfig.recipient_type
          const hasRecipientType = !!recipientType
          const hasPhone = recipientType !== 'custom_phone' || !!this.stepConfig.custom_phone
          const hasStaff = recipientType !== 'company_user_phone' ||
            (this.stepConfig.company_users && this.stepConfig.company_users.length > 0)
          // Opting into the fallback without choosing a template would save a
          // step that silently does nothing when SMS cannot be sent.
          const hasFallbackTemplate = !this.stepConfig.fallback_to_email ||
            !!this.stepConfig.fallback_email_template_id

          return hasSmsTemplate && hasRecipientType && hasPhone && hasStaff && hasFallbackTemplate
        }

        default:
          return false
      }
    }
  },

  async mounted() {
    // Check if editing existing automation
    if (this.$route.params.id) {
      this.isEditing = true
      this.automationId = this.$route.params.id
    }

    await this.loadInitialData()

    if (this.isEditing) {
      await this.loadAutomation()
    }
  },

  methods: {
    onScheduleTypeChange(type) {
      // Reset offset direction based on schedule type
      if (type === 'before_date') {
        this.stepConfig.offset.direction = 'before'
      } else if (type === 'after_date') {
        this.stepConfig.offset.direction = 'after'
      }

      // Clear reference field if switching to specific_time
      if (type === 'specific_time') {
        this.stepConfig.reference_field = ''
      }
    },

    onReferenceFieldChange(fieldKey) {
      const fixedFields = ['gift_card.delivery_date']
      if (fixedFields.includes(fieldKey)) {
        this.stepConfig.schedule_type = 'on_date'
      } else if (this.stepConfig.schedule_type === 'on_date') {
        // Switching away from a fixed field, reset to default
        this.stepConfig.schedule_type = 'before_date'
        this.stepConfig.offset.direction = 'before'
      }
    },

    getScheduleDirection(scheduleType) {
      return scheduleType === 'before_date' ? 'before' : 'after'
    },

    formatScheduleDescription(config) {
      let description = ''

      if (config.schedule_type === 'specific_time') {
        description = `At ${config.time_constraints.specific_time}`
      } else {
        const field = this.scheduleFields.find(f => f.field_key === config.reference_field)
        const fieldName = field ? field.field_name : config.reference_field

        if (config.schedule_type === 'on_date') {
          description = `On ${fieldName}`
        } else {
          description = `${config.offset.value} ${config.offset.unit} ${this.getScheduleDirection(config.schedule_type)} ${fieldName}`
        }

        if (config.time_constraints.specific_time) {
          description += ` at ${config.time_constraints.specific_time}`
        }
      }

      const constraints = []
      if (config.time_constraints.timezone !== 'UTC') {
        constraints.push(`${config.time_constraints.timezone} timezone`)
      }
      if (config.time_constraints.skip_weekends) {
        constraints.push('skip weekends')
      }
      if (config.time_constraints.business_hours_only) {
        constraints.push('business hours only')
      }

      if (constraints.length > 0) {
        description += ` (${constraints.join(', ')})`
      }

      return description
    },

    validateScheduleConfig(config) {
      // Validate required fields
      if (config.schedule_type === 'specific_time') {
        return !!config.time_constraints.specific_time
      }

      if (!config.reference_field) {
        return false
      }

      if (config.schedule_type !== 'on_date') {
        if (!config.offset.value || !config.offset.unit) {
          return false
        }
      }

      // Validate time format if specified
      if (config.time_constraints.specific_time) {
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
        if (!timeRegex.test(config.time_constraints.specific_time)) {
          return false
        }
      }

      return true
    },
    // New method to get field config
    getFieldConfig(fieldKey) {
      if (fieldKey === 'game_id') {
        return {
          field_key: 'game_id',
          field_name: 'Game',
          data_type: 'string'
        }
      }
      return this.filteredConditionFields.find(f => f.field_key === fieldKey) ||
        this.conditionFields.find(f => f.field_key === fieldKey)
    },

    formatUserLabel(user) {
      const nameParts = []
      if (user.first_name) nameParts.push(user.first_name)
      if (user.last_name) nameParts.push(user.last_name)

      if (nameParts.length === 0) {
        return user.email
      }
      return `${nameParts.join(' ')} (${user.email})`
    },

    getModalSize(type){
      if(type === 'condition'){
        return 'modal-lg'
      }
      return ''
    },

    onConditionFieldChange(condition, fieldKey) {
      // Special handling for game_id field
      if (fieldKey === 'booking.game_id') {
        condition.field = fieldKey
        condition.operator = 'equals' // Default operator for game
        condition.value = ''
        return
      }

      const field = this.filteredConditionFields.find(f => f.field_key === fieldKey) ||
        this.conditionFields.find(f => f.field_key === fieldKey)
      if (field) {
        condition.field = fieldKey
        condition.operator = 'equals' // Reset to default
        condition.value = ''

        // Update available operators (API returns these already parsed; older saved shapes may be strings)
        const ops = field.available_operators
        this.conditionBuilder.availableOperators = Array.isArray(ops) ? ops : JSON.parse(ops || '[]')

        // Update available values if field has predefined options
        const pv = field.possible_values
        const possibleValues = Array.isArray(pv) ? pv : JSON.parse(pv || 'null')
        this.conditionBuilder.availableValues = possibleValues || []
      }
    },

    // Handle operator change
    onConditionOperatorChange(condition, operator) {
      condition.operator = operator
      condition.value = '' // Reset value when operator changes
      // Reset between values when switching operators
      if (operator === 'between') {
        this.$set(condition, 'minValue', '')
        this.$set(condition, 'maxValue', '')
      }
    },

    // Update combined value for between operator
    updateBetweenValue(condition) {
      if (condition.minValue !== '' && condition.maxValue !== '') {
        condition.value = `${condition.minValue},${condition.maxValue}`
      } else {
        condition.value = ''
      }
    },

    // Validate condition configuration
    isConditionValid(condition) {
      if (condition.operator === 'between') {
        return condition.field &&
          condition.minValue !== '' && condition.minValue !== undefined &&
          condition.maxValue !== '' && condition.maxValue !== undefined &&
          Number(condition.minValue) <= Number(condition.maxValue)
      }
      return condition.field &&
        condition.operator &&
        (condition.value !== '' || condition.operator === 'exists' || condition.operator === 'not_exists')
    },

    // Get human readable condition description
    getConditionDescription(condition) {
      // Special handling for game field
      if (condition.field.includes('game_id')) {
        const game = this.games.find(g => g.id === condition.value)
        const gameName = game ? game.name : 'Unknown Game'
        const operator = this.formatOperator(condition.operator)
        return `Game ${operator} ${gameName}`
      }

      if (condition.field.includes('game_master_id')) {
        const cu = this.companyUsers.find(g => g.id === condition.value)
        const email = cu ? cu.email : 'Unknown GM'
        const operator = this.formatOperator(condition.operator)
        return `Game master ${operator} ${email}`
      }

      if (condition.field.includes('gift_card_type')) {
        const gct = this.giftCardTypes.find(g => g.id === condition.value)
        const giftCardType = gct ? gct.name : 'Unknown gift card type'
        const operator = this.formatOperator(condition.operator)
        return `Gift card type ${operator} ${giftCardType}`
      }

      const field = this.filteredConditionFields.find(f => f.field_key === condition.field) ||
        this.conditionFields.find(f => f.field_key === condition.field)
      const fieldName = field ? field.field_name : condition.field
      const operator = this.formatOperator(condition.operator)

      // Special handling for between operator
      if (condition.operator === 'between') {
        return `${fieldName} is between ${condition.minValue} and ${condition.maxValue}`
      }

      const value = this.formatValue(condition.value, field)

      return `${fieldName} ${operator} ${value}`
    },

    // Format operator for display
    formatOperator(operator) {
      const operatorMap = {
        'equals': 'is',
        'not_equals': 'is not',
        'contains': 'contains',
        'not_contains': 'does not contain',
        'greater_than': 'is greater than',
        'less_than': 'is less than',
        'greater_or_equal': 'is greater than or equal to',
        'less_or_equal': 'is less than or equal to',
        'in': 'is one of',
        'not_in': 'is not one of',
        'before': 'is before',
        'after': 'is after',
        'between': 'is between',
        'exists': 'exists',
        'not_exists': 'does not exist'
      }
      return operatorMap[operator] || operator
    },

    // Format value for display
    formatValue(value, field) {
      if (!field) return value

      if (field.data_type === 'boolean') {
        return value === 'true' ? 'Yes' : 'No'
      }

      if (field.data_type === 'date') {
        return new Date(value).toLocaleDateString()
      }

      return value
    },

    async getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => resolve(data.idToken.jwtToken))
          .catch(err => console.log(err))
      })
    },

    getSelectedItem() {
      // Implementation for getting selected item
      return null
    },

    deleteSelectedComponent() {
      this.selectedComponent = null
    },

    async loadInitialData() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        // Load trigger types, email templates, games, and users in parallel
        const [triggersResponse, templatesResponse, gamesResponse, usersResponse, giftCardTypeResponse] = await Promise.all([
          axios.get(`${API_LOCATION}email-automation-trigger-types`, config),
          axios.get(`${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates?count=1000&offset=0`, config),
          axios.get(`${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/games`, config),
          axios.get(`${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/company-users`, config),
          axios.get(`${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/gift-card-types`, config),
        ])

        this.triggerTypes = triggersResponse.data.trigger_types || []
        this.emailTemplates = templatesResponse.data.templates || []

        // SMS templates load separately and failure-tolerantly: a group with
        // SMS disabled (or an environment without the routes) must never
        // break the email builder.
        if (this.smsEnabled) {
          try {
            // status + count are explicit: the list endpoint paginates with a
            // default of 10, so an unparameterised fetch would silently hide
            // templates from this dropdown. Filtering server-side means the
            // 100 we ask for are 100 ACTIVE ones, rather than a page that
            // drafts could fill up.
            const smsResponse = await axios.get(
              `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-templates?status=active&count=100`,
              config
            )
            this.smsTemplates = (smsResponse.data.templates || []).filter(t => t.status === 'active')
          } catch (e) {
            console.log('Could not load SMS templates', e)
            this.smsTemplates = []
          }
        }
        this.games = gamesResponse.data || []
        this.conditionFields = triggersResponse.data.condition_fields || []
        this.scheduleFields = triggersResponse.data.schedule_fields || []
        this.companyUsers = usersResponse.data.company_users || []
        this.giftCardTypes = giftCardTypeResponse.data.gift_card_types || []

        // Roles only feed the "Select roles" recipient picker. They load
        // separately and failure-tolerantly: the endpoint is gated on
        // automation permissions via view=automation, but a rejection must
        // leave the picker empty rather than block the whole builder.
        try {
          const rolesResponse = await axios.get(
            `${API_LOCATION}companies/${this.companyId}/roles?view=automation`,
            config
          )
          const defaultRoles = rolesResponse.data.default_roles || []
          const customRoles = rolesResponse.data.roles || []
          this.companyRoles = defaultRoles.concat(customRoles).map(r => ({
            role_key: (r.default_role ? 'd' : 'c') + r.id,
            id: r.id,
            name: r.name,
            default_role: r.default_role ? 1 : 0
          }))
        } catch (e) {
          console.log('Could not load company roles', e)
          this.companyRoles = []
        }

      } catch (error) {
        console.error('Error loading initial data:', error)
        swal('Error!', 'Failed to load required data', 'error')
      } finally {
        this.loading = false
      }
    },
    getScheduleFields() {
      // If no trigger is selected, return all fields
      if (!this.triggerType) {
        return this.scheduleFields
      }

      // Get the category from the selected trigger
      const triggerCategory = this.triggerType.category

      // Trigger categories whose events carry a real customer (end user) with a
      // birthday on file. Customer-level global fields (e.g. "Customer's next
      // birthday") are offered here, not on staff/cart/project triggers.
      const customerBearingCategories = ['booking', 'transaction', 'payment', 'gift_card']

      // Filter schedule fields based on the trigger category
      return this.scheduleFields.filter(field => {
        // Field's own category matches the trigger category
        if (field.category === triggerCategory) {
          return true
        }
        // Customer-level global fields apply across any customer-bearing trigger
        if (field.is_global && customerBearingCategories.includes(triggerCategory)) {
          return true
        }
        return false
      }).sort((a, b) => a.field_name.localeCompare(b.field_name)) // Sort alphabetically by name
    },
    async loadAutomation() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        const response = await axios.get(
          `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations/${this.automationId}`,
          config
        )

        const automationData = response.data.automation

        // Transform the API response to match component structure
        this.automation = {
          ...automationData,
          // Convert trigger_type object to trigger_type_id
          trigger_type_id: automationData.trigger_type?.id || null,
          // Keep trigger_config as is (it's already correct)
          trigger_config: automationData.trigger_config || {}
        }

        // Ensure the trigger type exists in our triggerTypes array
        if (automationData.trigger_type && !this.triggerTypes.find(t => t.id === automationData.trigger_type.id)) {
          // Add the trigger type to our array if it's missing
          this.triggerTypes.push(automationData.trigger_type)
        }

      } catch (error) {
        console.error('Error loading automation:', error)
        swal('Error!', 'Failed to load automation', 'error')
      } finally {
        this.loading = false
      }
    },

    // Trigger methods
    openTriggerModal() {
      this.selectedTrigger = this.triggerType || null
      this.modals.trigger = true
    },

    applyTrigger() {
      if (this.selectedTrigger) {
        this.automation.trigger_type_id = this.selectedTrigger.id
        this.automation.trigger_config = {}
        this.modals.trigger = false
      }
    },

    removeTrigger() {
      this.automation.trigger_type_id = null
      this.automation.trigger_config = {}
    },

    // Step methods
    addStep(stepType) {
      this.currentStepType = { type: stepType, ...this.stepTypes[stepType] }
      this.stepConfig = JSON.parse(JSON.stringify(this.stepTypes[stepType].defaultConfig))
      this.editingStep = null
      this.editingStepIndex = null
      this.showAddStepMenu = false
      this.modals.stepConfig = true
    },

    editStep(step, index) {
      this.currentStepType = { type: step.step_type, ...this.stepTypes[step.step_type] }
      // Overlay the saved config onto the step type's defaults so keys added
      // after the step was saved exist when the object becomes reactive.
      const defaults = this.stepTypes[step.step_type]
        ? JSON.parse(JSON.stringify(this.stepTypes[step.step_type].defaultConfig))
        : {}
      this.stepConfig = { ...defaults, ...JSON.parse(JSON.stringify(step.step_config)) }
      this.editingStep = step
      this.editingStepIndex = index
      this.modals.stepConfig = true
    },

    removeStep(index) {
      this.automation.steps.splice(index, 1)
    },

    saveStepConfig() {
      const newStep = {
        temp_id: Date.now(), // Temporary ID for new steps
        step_order: this.automation.steps.length + 1,
        step_type: this.currentStepType.type,
        step_config: JSON.parse(JSON.stringify(this.stepConfig)),
        is_active: true
      }

      if (this.editingStep) {
        // Update existing step
        this.automation.steps[this.editingStepIndex] = {
          ...this.editingStep,
          step_config: JSON.parse(JSON.stringify(this.stepConfig))
        }
      } else {
        // Add new step
        this.automation.steps.push(newStep)
      }

      this.modals.stepConfig = false
      this.resetStepConfig()
    },

    resetStepConfig() {
      this.currentStepType = null
      this.stepConfig = {}
      this.editingStep = null
      this.editingStepIndex = null
    },

    // Condition methods
    addCondition() {
      this.stepConfig.conditions.push({
        field: '',
        operator: 'equals',
        value: '',
        value_type: 'static'
      })
    },

    removeCondition(index) {
      if (this.stepConfig.conditions.length > 1) {
        this.stepConfig.conditions.splice(index, 1)
      }
    },

    // Flow management
    clearFlow() {
      swal({
        title: 'Clear automation flow?',
        text: 'This will remove all steps from your automation.',
        type: 'warning',
        showCancelButton: true
      }).then((result) => {
        if (result.value) {
          this.automation.trigger_type_id = null
          this.automation.trigger_config = {}
          this.automation.steps = []
        }
      })
    },

    // Save methods
    async saveAsDraft() {
      await this.saveAutomation(false)
    },

    async saveAndActivate() {
      await this.saveAutomation(true)
    },

    async saveAutomation(activate = false) {
      if (!this.automation.name) {
        swal('Error!', 'Please enter an automation name', 'error')
        return
      }

      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        const automationData = {
          ...this.automation,
          is_active: activate
        }

        let response
        if (this.isEditing) {
          response = await axios.put(
            `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations/${this.automationId}`,
            automationData,
            config
          )
        } else {
          response = await axios.post(
            `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations`,
            automationData,
            config
          )

          if (response.data && response.data.automation && response.data.automation.id) {
            const newAutomationId = response.data.automation.id

            // Update local state to editing mode
            this.isEditing = true
            this.automationId = newAutomationId
            this.automation.id = newAutomationId

            // Update the URL to include the new automation ID
            this.$router.replace({
              path: `/mail/custom/automation/${newAutomationId}`
            })
          }
        }

        const action = activate ? 'activated' : 'saved'
        swal('Success!', `Automation ${action} successfully`, 'success')

      } catch (error) {
        console.error('Error saving automation:', error)

        // Check if the error response contains validation errors from the backend
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors

          // Handle array of error messages
          if (Array.isArray(errors) && errors.length > 0) {
            let errorMessage = ''

            if (errors.length === 1) {
              // Single error - show it directly
              errorMessage = errors[0]
            } else {
              // Multiple errors - join them with line breaks
              errorMessage = 'Please fix the following issues:\n\n'
              errorMessage += errors.map(err => `• ${err}`).join('\n')
            }

            swal('Validation Error', errorMessage, 'error')
          } else if (typeof errors === 'string') {
            // Single error message as string
            swal('Validation Error', errors, 'error')
          } else if (typeof errors === 'object') {
            // Handle object-based errors (field-specific errors)
            let errorMessage = 'Please fix the following issues: \n\n'

            Object.keys(errors).forEach(field => {
              const fieldErrors = errors[field]
              if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach(err => {
                  errorMessage += `• ${field}: ${err}\n`
                })
              } else {
                errorMessage += `• ${field}: ${fieldErrors}\n`
              }
            })

            swal('Validation Error', errorMessage.trim(), 'error')
          } else {
            // Fallback for unexpected error format
            swal('Error!', 'Failed to save automation', 'error')
          }
        } else if (error.response && error.response.data && error.response.data.message) {
          // Handle single message format
          swal('Error!', error.response.data.message, 'error')
        } else if (error.message) {
          // Handle standard JavaScript error
          swal('Error!', error.message, 'error')
        } else {
          // Generic fallback error
          swal('Error!', 'Failed to save automation. Please try again.', 'error')
        }
      } finally {
        this.loading = false
      }
    },

    // Helper methods
    getStepTypeConfig(stepType) {
      return this.stepTypes[stepType] || {}
    },

    getStepSummary(step) {
      switch (step.step_type) {
        case 'wait':
          return `Wait ${step.step_config.duration} ${step.step_config.unit}`

        case 'schedule':
          return this.formatScheduleDescription(step.step_config)

        case 'condition':
          const conditionCount = step.step_config.conditions?.length || 0
          let summary = `${conditionCount} condition${conditionCount !== 1 ? 's' : ''}`
          return summary

        case 'send_email':
          const template = this.emailTemplates.find(t => t.id === step.step_config.template_id)
          return template ? `Send "${template.name}"` : 'Email template not found'

        // Braced: the send_email case declares consts in the switch's block scope.
        case 'send_sms': {
          const smsTemplate = this.smsTemplates.find(t => t.id === step.step_config.sms_template_id)
          if (!smsTemplate) return 'SMS template not found'
          const recipient = {
            customer_phone: 'the customer',
            custom_phone: step.step_config.custom_phone || 'a custom number',
            company_user_phone: 'staff'
          }[step.step_config.recipient_type] || 'the customer'
          return `Text "${smsTemplate.name}" to ${recipient}`
        }

        default:
          return step.step_type
      }
    },

    getStepConfigData(step) {
      switch (step.step_type) {
        case 'wait':
          return {
            icon: 'tim-icons icon-time-alarm',
            text: `${step.step_config.duration} ${step.step_config.unit}`
          }
        case 'schedule':
          const scheduleText = []

          if (step.step_config.schedule_type === 'specific_time') {
            scheduleText.push(`At ${step.step_config.time_constraints.specific_time}`)
          } else {
            const field = this.scheduleFields.find(f => f.field_key === step.step_config.reference_field)
            const fieldName = field ? field.field_name : step.step_config.reference_field

            if (step.step_config.schedule_type === 'on_date') {
              scheduleText.push(`On ${fieldName}`)
            } else {
              scheduleText.push(`${step.step_config.offset.value} ${step.step_config.offset.unit} ${this.getScheduleDirection(step.step_config.schedule_type)} ${fieldName}`)
            }
          }

          if (step.step_config.time_constraints.specific_time && step.step_config.schedule_type !== 'specific_time') {
            scheduleText.push(`at ${step.step_config.time_constraints.specific_time}`)
          }

          if (step.step_config.time_constraints.skip_weekends) {
            scheduleText.push('Skip weekends')
          }

          return {
            icon: 'tim-icons icon-calendar-60',
            text: scheduleText.join('\n')
          }
        case 'condition':
          const conditionTexts = step.step_config.conditions.map(condition => {
            // Handle game conditions
            if (condition.field === 'game_id') {
              const game = this.games.find(g => g.id === condition.value)
              const gameName = game ? game.name : 'Unknown Game'
              return `Game ${this.formatOperator(condition.operator)} ${gameName}`
            }
            return this.getConditionDescription(condition)
          }).join('\n')

          return {
            icon: 'tim-icons icon-decision',
            text: conditionTexts
          }
        case 'send_email':
          let emailText = ''
          if (step.step_config.to_field === 'customer_email') {
            emailText = 'Customer email'
          } else if (step.step_config.to_field === 'company_email') {
            const parts = []
            if (step.step_config.company_users && Array.isArray(step.step_config.company_users) && step.step_config.company_users.length > 0) {
              parts.push(`Company users: ${step.step_config.company_users.map(user => user.email).join(', ')}`)
            }
            if (step.step_config.company_roles && Array.isArray(step.step_config.company_roles) && step.step_config.company_roles.length > 0) {
              parts.push(`Roles: ${step.step_config.company_roles.map(role => role.name).join(', ')}`)
            }
            emailText = parts.length > 0 ? parts.join('\n') : 'Company users: Not set'
          } else if (step.step_config.to_field === 'ticket_watchers') {
            emailText = 'Ticket watchers'
          } else if (step.step_config.to_field === 'ticket_assignee') {
            emailText = 'Ticket assignee'
          } else if (step.step_config.to_field === 'ticket_reporter') {
            emailText = 'Ticket reporter'
          } else if (step.step_config.to_field === 'gift_card_recipient') {
            emailText = 'Gift card recipient email'
          } else {
            emailText = `Custom: ${step.step_config.custom_email || 'Not set'}`
          }

          return {
            icon: 'tim-icons icon-email-85',
            text: emailText
          }

        // Braced: the send_email case declares `let emailText` in this switch's
        // block scope.
        case 'send_sms': {
          let smsText = ''
          if (step.step_config.recipient_type === 'custom_phone') {
            smsText = `Custom: ${step.step_config.custom_phone || 'Not set'}`
          } else if (step.step_config.recipient_type === 'company_user_phone') {
            const users = step.step_config.company_users
            smsText = Array.isArray(users) && users.length > 0
              ? `Staff: ${users.map(u => u.name || u.email || u).join(', ')}`
              : 'Staff: Not set'
          } else {
            smsText = 'Customer phone'
          }
          if (step.step_config.fallback_to_email) {
            smsText += '\nFalls back to email if the text cannot be sent'
          }
          return {
            icon: 'tim-icons icon-chat-33',
            text: smsText
          }
        }

        default:
          return { icon: '', text: '' }
      }
    },

    isStepComplete(step) {
      switch (step.step_type) {
        case 'wait':
          return step.step_config.duration && step.step_config.unit
        case 'schedule':
          return this.validateScheduleConfig(step.step_config)
        case 'condition':
          return step.step_config.conditions && step.step_config.conditions.length > 0
        case 'send_email':
          return step.step_config.template_id && step.step_config.to_field
        case 'send_sms':
          return step.step_config.sms_template_id && step.step_config.recipient_type
        default:
          return false
      }
    },

    formatCategoryName(category) {
      return category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')
    },

    getCategoryIcon(category) {
      const icons = {
        booking: 'tim-icons icon-calendar-60',
        payment: 'tim-icons icon-credit-card',
        customer: 'tim-icons icon-single-02',
        transaction: 'tim-icons icon-transaction',
        cart: 'tim-icons icon-cart',
        gift_card: 'tim-icons icon-gift-2',
        promo_code: 'tim-icons icon-tag',
        inventory: 'tim-icons icon-inventory',
        survey: 'tim-icons icon-chat-33',
        photo: 'tim-icons icon-image-02',
        game: 'tim-icons icon-trophy',
        project: 'tim-icons icon-app'
      }
      return icons[category] || 'tim-icons icon-bell-55'
    }
  }
}
</script>

<style scoped>
/* Email Builder Wrapper - matches EmailTemplateBuilder */
.automation-builder-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
  /*background: #27293d;*/
}

/* Breadcrumb Section */
.breadcrumb-section {
  /*background: #1e1e2e;*/
  flex-shrink: 0;
}

.automation-builder {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Builder Header - similar to EmailTemplateBuilder */
.builder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /*background: #1e1e2e;*/
  border-bottom: 1px solid #393c54;
  padding: 1rem 1.5rem 0.5rem 1.5rem;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.automation-info {
  display: flex;
  flex-direction: column;
}

.automation-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  /*color: #ffffff;*/
}

.automation-status {
  font-size: 0.875rem;
  /*color: rgba(255, 255, 255, 0.6);*/
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.mobile-sidebar-controls {
  display: none;
}

/* Show mobile controls and hide desktop sidebars on mobile */
@media (max-width: 1024px) {
  .mobile-sidebar-controls {
    display: flex;
    gap: 0.5rem;
    margin-right: 0.5rem;
  }

  .mobile-only {
    display: inline-flex !important;
  }

  .desktop-only {
    display: none !important;
  }
}

/* Main Builder Container - grid layout like EmailTemplateBuilder */
.builder-container {
  display: grid;
  grid-template-columns: 320px 1fr 320px;
  gap: 0;
  flex: 1;
  overflow: hidden;
}

/* Mobile responsive grid */
@media (max-width: 1024px) {
  .builder-container {
    grid-template-columns: 1fr;
  }
}

/* Sidebars - consistent with EmailTemplateBuilder */
.components-sidebar,
.properties-sidebar {
  /*background: #1e1e2e;*/
  border-right: 1px solid #393c54;
  overflow-y: auto;
  padding: 1.5rem;
}

.properties-sidebar {
  border-left: 1px solid #393c54;
  border-right: none;
}

.sidebar-section {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

/* Component Groups */
.component-group {
  margin-bottom: 2rem;
}

.group-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
}

.group-subtitle {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1rem;
  margin-top: 0;
}

/* Components Grid - simpler layout */
.components-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 0.75rem;
}

.components-grid.mobile {
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

@media (max-width: 576px) {
  .components-grid.mobile {
    grid-template-columns: 1fr;
  }
}

.component-item {
  /*background: #27293d;*/
  border: 1px solid #393c54;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.component-item:hover:not(.disabled) {
  border-color: #e14eca;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(225, 78, 202, 0.3);
}

.component-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.component-item.selected {
  background: rgba(225, 78, 202, 0.15);
  border-color: #e14eca;
}

.component-item i {
  font-size: 1.5rem;
  color: #e14eca;
}

.component-item span {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Trigger component special styling */
.trigger-component {
  grid-column: 1 / -1;
  flex-direction: row;
  text-align: left;
  justify-content: space-between;
  padding: 1.25rem;
}

.component-content {
  flex: 1;
  margin-left: 1rem;
}

.component-name {
  display: block;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.25rem;
}

.component-desc {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
}

.component-status {
  flex-shrink: 0;
}

/* Mobile Modal Styling */
.mobile-sidebar-modal .modal-dialog {
  margin: 1rem;
  max-width: none;
  width: calc(100% - 2rem);
}

.mobile-sidebar-modal .modal-content {
  background: #171826;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.75rem;
}

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  /*color: #ffffff;*/
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-title i {
  color: #e14eca;
}

.mobile-components-content,
.mobile-settings-content {
  padding: 0.5rem 0;
  max-height: 60vh;
  overflow-y: auto;
}

/* Canvas Area - matches EmailTemplateBuilder structure */
.canvas-area {
  /*background: #27293d;*/
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  /*background: #1e1e2e;*/
  border-bottom: 1px solid #393c54;
  flex-shrink: 0;
}

.canvas-title h5 {
  color: #fff;
  font-weight: 600;
  margin: 0 0 0.25rem;
  display: flex;
  align-items: center;
}

.canvas-title h5 i {
  color: #e14eca;
}

.canvas-title small {
  color: rgba(255, 255, 255, 0.7);
}

.canvas-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  /*background: #27293d;*/
}

/* Enhanced Empty State */
.empty-flow {
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.9);
  /*background: #1e1e2e;*/
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  margin: 2rem auto;
  max-width: 500px;
}

.empty-illustration {
  position: relative;
  margin-bottom: 2rem;
}

.empty-illustration i {
  font-size: 4rem;
  color: #e14eca;
  position: relative;
  z-index: 2;
}

.empty-flow h4 {
  /*color: #ffffff;*/
  margin-bottom: 1rem;
  font-weight: 600;
}

.empty-flow p {
  margin-bottom: 2rem;
  /*color: rgba(255, 255, 255, 0.8);*/
  font-size: 1rem;
  line-height: 1.5;
}

/* Flow Canvas - simplified without intermediary card */
.flow-canvas {
  margin: 0;
  max-width: none;
  background: transparent;
  border: none;
  border-radius: 0;
  overflow: visible;
}

.flow-container {
  padding: 1rem 2rem;
  max-width: 600px;
  margin: 0 auto;
}

/* Flow Steps - improved contrast and visibility */
.flow-step {
  margin-bottom: 1.5rem;
}

.step-content {
  /*background: #1e1e2e;*/
  border: 2px solid #393c54;
  border-radius: 0.75rem;
  padding: 1.25rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.step-content:hover {
  /*background: #252640;*/
  border-color: #e14eca;
  box-shadow: 0 8px 25px rgba(225, 78, 202, 0.3);
  transform: translateY(-2px);
}

.trigger-step .step-content {
  border-color: #e14eca;
  /*background: #1e1e2e;*/
  box-shadow: 0 4px 12px rgba(225, 78, 202, 0.2);
}

.trigger-step .step-content:hover {
  /*background: #252640;*/
  box-shadow: 0 8px 25px rgba(225, 78, 202, 0.4);
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.step-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
  color: white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
}

.step-icon.trigger {
  background: linear-gradient(135deg, #e14eca, #ba54f5);
  box-shadow: 0 4px 12px rgba(225, 78, 202, 0.4);
}

.step-icon.warning {
  background: linear-gradient(135deg, #fd5d93, #ec250d);
  box-shadow: 0 4px 12px rgba(253, 93, 147, 0.4);
}

.step-icon.info {
  background: linear-gradient(135deg, #1171ef, #11cdef);
  box-shadow: 0 4px 12px rgba(17, 113, 239, 0.4);
}

.step-icon.success {
  background: linear-gradient(135deg, #00bf9a, #5e72e4);
  box-shadow: 0 4px 12px rgba(0, 191, 154, 0.4);
}

.step-info {
  flex: 1;
}

.step-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
}

/* Fixed text contrast - much more visible now */
.step-info h6 {
  /*color: #ffffff;*/
  font-weight: 600;
  margin: 0;
  font-size: 1rem;
}

.step-badge {
  font-size: 0.625rem;
  padding: 0.25rem 0.5rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Improved description visibility */
.step-description {
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
  font-size: 0.875rem;
  font-weight: 400;
}

.step-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.step-content:hover .step-actions {
  opacity: 1;
}

.step-config {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.config-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(225, 78, 202, 0.15);
  color: #ffffff;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(225, 78, 202, 0.3);
}

.config-badge i {
  color: #e14eca;
}

.config-preview {
  background: rgba(255, 255, 255, 0.08);
  white-space: pre-line;
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 500;
}

/* Flow Connectors - enhanced visibility */
.flow-connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1.25rem 0;
  position: relative;
}

.connector-line {
  width: 3px;
  height: 40px;
  background: linear-gradient(to bottom,
  rgba(225, 78, 202, 0.8),
  rgba(225, 78, 202, 0.4)
  );
  border-radius: 1.5px;
  position: relative;
}

.connector-line::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
}

.connector-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e14eca;
  margin-top: -5px;
  box-shadow:
    0 0 12px rgba(225, 78, 202, 0.6),
    0 0 4px rgba(225, 78, 202, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

/* Add Step Area - improved styling */
.add-step-area {
  text-align: center;
  margin-top: 2rem;
  position: relative;
}

.add-step-button {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  /*background: #1e1e2e;*/
  border: 2px dashed rgba(225, 78, 202, 0.6);
  border-radius: 0.75rem;
  padding: 1.25rem 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  /*color: #ffffff;*/
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.add-step-button:hover {
  /*background: #252640;*/
  border-color: #e14eca;
  /*color: #ffffff;*/
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(225, 78, 202, 0.3);
}

.add-step-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e14eca, #ba54f5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.125rem;
  box-shadow: 0 3px 8px rgba(225, 78, 202, 0.3);
}

.add-step-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  /* Floating menu must be opaque; it overlays the flow on both desktop and mobile */
  background: #171826;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.75rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  z-index: 10;
  margin-top: 0.75rem;
  min-width: 280px;
  overflow: hidden;
}

.step-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.step-option:last-child {
  border-bottom: none;
}

.step-option:hover {
  background: rgba(225, 78, 202, 0.15);
}

.option-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  color: white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
}

.option-icon.warning {
  background: linear-gradient(135deg, #fd5d93, #ec250d);
}

.option-icon.info {
  background: linear-gradient(135deg, #1171ef, #11cdef);
}

.option-icon.success {
  background: linear-gradient(135deg, #00bf9a, #5e72e4);
}

/* Improved text visibility in menu options */
.option-content span {
  display: block;
  font-weight: 600;
  /*color: #ffffff;*/
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.option-content small {
  /*color: rgba(255, 255, 255, 0.8);*/
  font-size: 0.75rem;
}

/* Trigger Modal */
.trigger-categories {
  max-height: 500px;
}

.category-list {
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  padding-right: 1rem;
  max-height: 400px;
  overflow-y: auto;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.5rem;
}

.category-item:hover,
.category-item.active {
  background: rgba(225, 78, 202, 0.1);
  border: 1px solid rgba(225, 78, 202, 0.3);
}

.category-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(225, 78, 202, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e14eca;
  font-size: 1rem;
}

.category-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.category-name {
  color: #fff;
  font-weight: 500;
}

.category-count {
  font-size: 0.75rem;
}

.triggers-list {
  max-height: 400px;
  overflow-y: auto;
  padding-left: 1rem;
}

.trigger-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.trigger-option:hover,
.trigger-option.selected {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.trigger-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.trigger-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(225, 78, 202, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e14eca;
  font-size: 1.125rem;
}

.trigger-details h6 {
  margin: 0 0 0.25rem 0;
  color: #fff;
}

.trigger-details p {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
}

.trigger-select {
  color: #e14eca;
  font-size: 1.25rem;
}

/* Step Configuration */
.config-section {
  margin-bottom: 2rem;
}

.config-header {
  margin-bottom: 1.5rem;
}

.config-header h6 {
  color: #fff;
  font-weight: 600;
  margin: 0 0 0.5rem;
  display: flex;
  align-items: center;
}

.config-header h6 i {
  color: #e14eca;
}

.config-header small {
  color: rgba(255, 255, 255, 0.7);
}

/* Form styling for sidebar */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

/* Modal enhancements */
.modal-offset-left .modal-dialog {
  margin-left: 140px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  /* The 140px left offset shoves the modal off-screen on phones */
  .modal-offset-left .modal-dialog {
    margin-left: 0;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .mobile-sidebar-controls {
    order: -1;
    margin-right: 0;
    margin-bottom: 0.5rem;
  }

  .trigger-categories .row {
    flex-direction: column;
  }

  .category-list {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-right: 0;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    max-height: 200px;
  }

  .triggers-list {
    padding-left: 0;
  }

  .flow-container {
    padding: 1rem;
  }

  .step-content {
    padding: 1rem;
  }

  .step-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .step-title-row {
    width: 100%;
    justify-content: space-between;
  }

  .step-actions {
    align-self: flex-end;
  }

  .add-step-menu {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 320px;
  }

  .canvas-header {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

@media (max-width: 576px) {
  .builder-header {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .header-actions {
    width: 100%;
  }

  .mobile-sidebar-controls {
    width: 100%;
  }

  .empty-flow {
    padding: 2rem 1rem;
    margin: 1rem;
  }

  .empty-flow h4 {
    font-size: 1.125rem;
  }

  .empty-illustration i {
    font-size: 3rem;
  }

  .step-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  .step-info h6 {
    font-size: 0.9rem;
  }

  .step-description {
    font-size: 0.8rem;
  }

  .add-step-button {
    padding: 1rem 1.5rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .add-step-icon {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
}

/* Custom Scrollbars */
.components-sidebar::-webkit-scrollbar,
.properties-sidebar::-webkit-scrollbar,
.canvas-content::-webkit-scrollbar,
.mobile-components-content::-webkit-scrollbar,
.mobile-settings-content::-webkit-scrollbar {
  width: 6px;
}

.components-sidebar::-webkit-scrollbar-track,
.properties-sidebar::-webkit-scrollbar-track,
.canvas-content::-webkit-scrollbar-track,
.mobile-components-content::-webkit-scrollbar-track,
.mobile-settings-content::-webkit-scrollbar-track {
  /*background: #1e1e2e;*/
  border-radius: 3px;
}

.components-sidebar::-webkit-scrollbar-thumb,
.properties-sidebar::-webkit-scrollbar-thumb,
.canvas-content::-webkit-scrollbar-thumb,
.mobile-components-content::-webkit-scrollbar-thumb,
.mobile-settings-content::-webkit-scrollbar-thumb {
  background: #393c54;
  border-radius: 3px;
  transition: background 0.2s;
}

.components-sidebar::-webkit-scrollbar-thumb:hover,
.properties-sidebar::-webkit-scrollbar-thumb:hover,
.canvas-content::-webkit-scrollbar-thumb:hover,
.mobile-components-content::-webkit-scrollbar-thumb:hover,
.mobile-settings-content::-webkit-scrollbar-thumb:hover {
  background: #e14eca;
}

/* Override any inherited scrollbar styles */
.components-sidebar,
.properties-sidebar,
.canvas-content,
.mobile-components-content,
.mobile-settings-content {
  scrollbar-width: thin;
  scrollbar-color: #393c54 #171826;
}

.components-sidebar:hover,
.properties-sidebar:hover,
.canvas-content:hover,
.mobile-components-content:hover,
.mobile-settings-content:hover {
  scrollbar-color: #e14eca #171826;
}

.conditions-container {
  margin-bottom: 2rem;
}

.condition-rule {
  /*background: rgba(255, 255, 255, 0.03);*/
  border: 2px solid rgba(225, 78, 202, 0.3);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
}

.condition-rule:hover {
  /*background: rgba(255, 255, 255, 0.05);*/
  border-color:#e14eca;
  box-shadow: 0 4px 15px rgba(225, 78, 202, 0.15);
}

.condition-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.56);
}

.condition-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e14eca, #ba54f5);
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(225, 78, 202, 0.3);
}

.condition-label {
  font-size: 1rem;
  font-weight: 600;
  /*color: #ffffff;*/

  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.condition-builder {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  align-items: end;
}

.condition-field,
.condition-operator,
.condition-value {
  display: flex;
  flex-direction: column;
}

.between-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.between-inputs .form-control {
  width: 100px;
}

.between-separator {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.condition-preview {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 191, 154, 0.15);
  border: 1px solid rgba(0, 191, 154, 0.42);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.condition-text {
  /*color: #ffffff;*/
  font-weight: 500;
}

.section-divider h6 {
  /*color: #ffffff;*/
  font-weight: 600;
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
}

.section-divider small {
  /*color: rgba(255, 255, 255, 0.7);*/
  font-size: 0.875rem;
}

/* Enhanced Select Styling for Condition Fields */
.el-select .el-input .el-input__inner {
  /*background: #27293d !important;*/
  border: 2px solid rgba(255, 255, 255, 0.2) !important;
  /*color: #ffffff !important;*/
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.el-select .el-input .el-input__inner:focus,
.el-select .el-input.is-focus .el-input__inner {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2) !important;
}

.el-select-dropdown {
  /*background: #1e1e2e !important;*/
  border: 2px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 0.75rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
  margin-top: 0.5rem;
}

.el-option {
  background: transparent !important;
  /*color: #ffffff !important;*/
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  margin: 0.25rem;
  transition: all 0.2s ease;
}

.el-option:hover {
  background: rgba(225, 78, 202, 0.15) !important;
  /*color: #ffffff !important;*/
}

.el-option.selected {
  background: rgba(225, 78, 202, 0.25) !important;
  /*color: #ffffff !important;*/
}

.el-option-group__title {
  /*color: rgba(255, 255, 255, 0.8) !important;*/
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 0.25rem;
}

.el-option .text-muted {
  /*color: rgba(255, 255, 255, 0.6) !important;*/
  font-size: 0.75rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .condition-builder {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .condition-header {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .condition-rule {
    padding: 1rem;
  }
}

/* Animation for condition rule addition/removal */
.condition-rule {
  animation: slideInDown 0.3s ease;
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Enhanced form controls */
.form-control {
  /*background: #27293d !important;*/
  border: 2px solid rgba(255, 255, 255, 0.2) !important;
  /*color: #ffffff !important;*/
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  /*background: #27293d !important;*/
  border-color: #e14eca !important;
  /*color: #ffffff !important;*/
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2) !important;
}

.form-control::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Add these styles to your <style scoped> section */

/* Schedule Icon Styling */
.step-icon.primary {
  background: linear-gradient(135deg, #e14eca, #ba54f5);
  box-shadow: 0 4px 12px rgba(225, 78, 202, 0.4);
}

.option-icon.primary {
  background: linear-gradient(135deg, #e14eca, #ba54f5);
}

/* Schedule Preview */
.schedule-preview {
  margin-top: 1.5rem;
}

.preview-card {
  background: rgba(225, 78, 202, 0.1);
  border: 2px solid rgba(225, 78, 202, 0.3);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

.preview-card:hover {
  background: rgba(225, 78, 202, 0.15);
  border-color: #e14eca;
  box-shadow: 0 4px 15px rgba(225, 78, 202, 0.2);
}

.preview-card i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.preview-text {
  font-size: 0.875rem;
  line-height: 1.4;
  font-weight: 500;
}

/* ---------- Light mode (.white-content) ---------- */
/* The builder hardcodes dark-theme text, borders and a few backgrounds; remap them
   so the page is legible on the light theme. Gradient/tinted circles intentionally
   keep their white glyphs, so they are left alone. */

/* Hardcoded light text -> dark */
.white-content .section-title,
.white-content .component-item,
.white-content .empty-flow,
.white-content .step-description { color: rgba(0, 0, 0, 0.7); }

.white-content .group-title,
.white-content .component-name,
.white-content .canvas-title h5,
.white-content .category-name,
.white-content .trigger-details h6,
.white-content .config-header h6,
.white-content .schedule-constraints-section h6,
.white-content .config-badge { color: rgba(0, 0, 0, 0.85); }

.white-content .group-subtitle,
.white-content .component-desc,
.white-content .canvas-title small,
.white-content .trigger-details p,
.white-content .config-header small { color: rgba(0, 0, 0, 0.55); }

/* Dark slate borders -> light dividers */
.white-content .builder-header,
.white-content .canvas-header,
.white-content .modal-header-custom,
.white-content .step-option { border-bottom-color: rgba(0, 0, 0, 0.1); }

.white-content .components-sidebar { border-right-color: rgba(0, 0, 0, 0.1); }
.white-content .properties-sidebar { border-left-color: rgba(0, 0, 0, 0.1); }
.white-content .category-list { border-right-color: rgba(0, 0, 0, 0.1); }

.white-content .component-item,
.white-content .trigger-option,
.white-content .add-step-menu,
.white-content .config-preview,
.white-content .step-content { border-color: rgba(0, 0, 0, 0.12); }
/* keep the trigger step's pink accent border */
.white-content .trigger-step .step-content { border-color: #e14eca; }

/* opaque white surface for the floating add-step menu in light mode */
.white-content .add-step-menu { background: #ffffff; }

.white-content .step-config { border-top-color: rgba(0, 0, 0, 0.12); }
.white-content .condition-header { border-bottom-color: rgba(0, 0, 0, 0.15); }

/* Faint white-on-white surfaces -> faint dark tints */
.white-content .config-preview,
.white-content .schedule-constraints-section { background: rgba(0, 0, 0, 0.03); }
.white-content .schedule-constraints-section { border-color: rgba(0, 0, 0, 0.1); }

/* Form controls hardcode a white border (rgba(255,255,255,0.2)) that vanishes on
   light; remap the border + placeholder so fields stay outlined and legible. */
.white-content .form-control,
.white-content input[type="time"] { border-color: rgba(0, 0, 0, 0.2) !important; }
.white-content .form-control::placeholder { color: rgba(0, 0, 0, 0.4) !important; }

/* Hardcoded dark backgrounds -> light surfaces */
.white-content .mobile-sidebar-modal .modal-content {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
}
.white-content input[type="time"] {
  background: #ffffff !important;
  color: rgba(0, 0, 0, 0.85) !important;
}
.white-content input[type="time"]::-webkit-calendar-picker-indicator { filter: none; }

/* Scrollbars */
.white-content .components-sidebar,
.white-content .properties-sidebar,
.white-content .canvas-content,
.white-content .mobile-components-content,
.white-content .mobile-settings-content { scrollbar-color: rgba(0, 0, 0, 0.3) transparent; }

.white-content .components-sidebar::-webkit-scrollbar-thumb,
.white-content .properties-sidebar::-webkit-scrollbar-thumb,
.white-content .canvas-content::-webkit-scrollbar-thumb,
.white-content .mobile-components-content::-webkit-scrollbar-thumb,
.white-content .mobile-settings-content::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.25); }

/* Send SMS step config */
.sms-step-hint {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.75rem;
  line-height: 1.4;
  margin-top: 0.35rem;
}
.sms-step-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
